import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";

export interface GraphNode {
  node_id: string;
  node_type: string;
  label: string;
  source_file: string | null;
  properties: Record<string, unknown>;
  repo_id: string | null;
}

export interface GraphEdge {
  source: string;
  target: string;
  edge_type: string;
  weight: number;
  properties: Record<string, unknown>;
}

export interface GraphSeedResponse {
  nodes: GraphNode[];
  edges: GraphEdge[];
  total_nodes: number;
  has_more: boolean;
}

export interface GraphExpandResponse {
  nodes: GraphNode[];
  edges: GraphEdge[];
  center_node: string;
  total_neighbors: number;
  has_more: boolean;
}

export function useMemoryGraph(projectSlug: string | null) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.memory.graphSeed(projectSlug ?? "");

  const query = useQuery({
    queryKey,
    queryFn: () =>
      api<GraphSeedResponse>(`/api/v2/projects/${projectSlug}/memory/graph/seed`),
    enabled: projectSlug !== null,
  });

  const expandMutation = useMutation({
    mutationFn: (nodeId: string) =>
      api<GraphExpandResponse>(
        `/api/v2/projects/${projectSlug}/memory/graph/expand`,
        {
          method: "POST",
          body: JSON.stringify({ node_id: nodeId, hops: 1, limit: 100 }),
        },
      ),
    onSuccess: (resp) => {
      queryClient.setQueryData<GraphSeedResponse>(queryKey, (prev) => {
        if (!prev) return prev;
        const existingIds = new Set(prev.nodes.map((n) => n.node_id));
        const newNodes = resp.nodes.filter((n) => !existingIds.has(n.node_id));
        // Dedupe edges by source|target|edge_type to prevent duplication when the
        // same node is expanded multiple times (e.g. onNavigate back-and-forth).
        const existingEdgeKeys = new Set(
          prev.edges.map((e) => `${e.source}|${e.target}|${e.edge_type}`),
        );
        const newEdges = resp.edges.filter(
          (e) => !existingEdgeKeys.has(`${e.source}|${e.target}|${e.edge_type}`),
        );
        return {
          ...prev,
          nodes: [...prev.nodes, ...newNodes],
          edges: [...prev.edges, ...newEdges],
        };
      });
    },
  });

  const expand = useCallback(
    async (nodeId: string) => {
      if (!projectSlug) return;
      await expandMutation.mutateAsync(nodeId);
    },
    [projectSlug, expandMutation],
  );

  return {
    graph: query.data ?? null,
    loading: query.isLoading,
    error: query.error ?? null,
    expand,
  };
}
